/**
 * Status Helper Functions
 *
 * Type-safe status comparison and checking utilities.
 * Use these instead of string comparisons for better type safety.
 */

import {
  normalizeLeaseStatus,
  type LeaseStatus,
  type PropertyStatus,
  type TenantStatus,
  type WorkOrderStatus,
  type BillStatus,
  type ReceivableStatus,
  type PaymentStatus,
  type ApplicationStatus,
  type LeadStatus,
  type ListingStatus,
  type PurchaseOrderStatus,
  type PaymentPlanStatus,
  type InstallmentStatus,
  type VendorStatus,
  type OwnerStatus,
  type UnitStatus,
} from '@repo/shared-types';

// ============================================================================
// Lease Status Helpers
// ============================================================================

/**
 * Check if a lease is active
 */
export function isLeaseActive(status: LeaseStatus): boolean {
  return status === 'Active';
}

/**
 * Check if a lease is expired
 */
export function isLeaseExpired(status: LeaseStatus): boolean {
  return status === 'Expired';
}

/** True when the lease is in the out-for-signature phase (legacy `Pending Signature` normalized). */
export function isLeaseOutForSignature(status: LeaseStatus | string): boolean {
  return normalizeLeaseStatus(String(status)) === 'Out_For_Signature';
}

/** @deprecated Use `isLeaseOutForSignature` */
export function isLeasePendingSignature(status: LeaseStatus | string): boolean {
  return isLeaseOutForSignature(status);
}

export function isLeaseExecuted(status: LeaseStatus | string): boolean {
  return status === 'Executed';
}

/** Human-readable label for lease status badges (underscores → phrasing). */
export function formatLeaseStatusLabel(status: string): string {
  if (status === 'Out_For_Signature') return 'Out for signature';
  return status;
}

// ============================================================================
// Property Status Helpers
// ============================================================================

/**
 * Check if a property is stable
 */
export function isPropertyStable(status: PropertyStatus): boolean {
  return status === 'Stable';
}

/**
 * Check if a property is at risk
 */
export function isPropertyAtRisk(status: PropertyStatus): boolean {
  return status === 'At Risk';
}

/**
 * Check if a property is vacant
 */
export function isPropertyVacant(status: PropertyStatus): boolean {
  return status === 'Vacant';
}

// ============================================================================
// Tenant Status Helpers
// ============================================================================

/**
 * Check if a tenant is current
 */
export function isTenantCurrent(status: TenantStatus): boolean {
  return status === 'Current';
}

/**
 * Check if a tenant has given notice
 */
export function isTenantNotice(status: TenantStatus): boolean {
  return status === 'Notice';
}

// ============================================================================
// Work Order Status Helpers
// ============================================================================

/**
 * Check if a work order is active (not completed/cancelled)
 */
export function isWorkOrderActive(status: WorkOrderStatus): boolean {
  return status !== 'Completed' && status !== 'Cancelled' && status !== 'Verified';
}

/**
 * Check if a work order is completed
 */
export function isWorkOrderCompleted(status: WorkOrderStatus): boolean {
  return status === 'Completed' || status === 'Verified';
}

/**
 * Check if a work order is open
 */
export function isWorkOrderOpen(status: WorkOrderStatus): boolean {
  return status === 'Open';
}

/**
 * Check if a work order is in progress
 */
export function isWorkOrderInProgress(status: WorkOrderStatus): boolean {
  return status === 'In Progress' || status === 'Vendor Assigned';
}

/**
 * Check if a work order requires approval
 */
export function isWorkOrderPendingApproval(status: WorkOrderStatus): boolean {
  return status === 'Pending Approval';
}

// ============================================================================
// Bill Status Helpers
// ============================================================================

/**
 * Check if a bill is paid
 */
export function isBillPaid(status: BillStatus): boolean {
  return status === 'Paid';
}

/**
 * Check if a bill is approved
 */
export function isBillApproved(status: BillStatus): boolean {
  return status === 'Approved';
}

/**
 * Check if a bill is pending approval
 */
export function isBillPendingApproval(status: BillStatus): boolean {
  return status === 'Pending Approval';
}

/**
 * Check if a bill is overdue
 */
export function isBillOverdue(status: BillStatus): boolean {
  return status === 'Overdue';
}

// ============================================================================
// Receivable Status Helpers
// ============================================================================

/**
 * Check if a receivable is paid
 */
export function isReceivablePaid(status: ReceivableStatus): boolean {
  return status === 'Paid';
}

/**
 * Check if a receivable is unpaid
 */
export function isReceivableUnpaid(status: ReceivableStatus): boolean {
  return status === 'Unpaid';
}

/**
 * Check if a receivable is late
 */
export function isReceivableLate(status: ReceivableStatus): boolean {
  return status === 'Late';
}

// ============================================================================
// Payment Status Helpers
// ============================================================================

/**
 * Check if a payment is completed
 */
export function isPaymentCompleted(status: PaymentStatus): boolean {
  return status === 'Completed' || status === 'Paid' || status === 'Cleared';
}

/**
 * Check if a payment is pending
 */
export function isPaymentPending(status: PaymentStatus): boolean {
  return status === 'Pending' || status === 'Processing';
}

/**
 * Check if a payment failed
 */
export function isPaymentFailed(status: PaymentStatus): boolean {
  return status === 'Failed';
}

// ============================================================================
// Application Status Helpers
// ============================================================================

/**
 * Check if an application is approved
 */
export function isApplicationApproved(status: ApplicationStatus): boolean {
  return status === 'Approved' || status === 'Approved - Pending Lease';
}

/**
 * Check if an application is denied
 */
export function isApplicationDenied(status: ApplicationStatus): boolean {
  return status === 'Denied' || status === 'Rejected';
}

/**
 * Check if an application is pending
 */
export function isApplicationPending(status: ApplicationStatus): boolean {
  return status === 'Pending' || status === 'Under Review' || status === 'Screening' || status === 'Pending Docs';
}

// ============================================================================
// Lead Status Helpers
// ============================================================================

/**
 * Check if a lead is new
 */
export function isLeadNew(status: LeadStatus): boolean {
  return status === 'New Inquiry';
}

/**
 * Check if a lead is leased
 */
export function isLeadLeased(status: LeadStatus): boolean {
  return status === 'Leased';
}

/**
 * Check if a lead is lost
 */
export function isLeadLost(status: LeadStatus): boolean {
  return status === 'Lost';
}

// ============================================================================
// Listing Status Helpers
// ============================================================================

/**
 * Check if a listing is active
 */
export function isListingActive(status: ListingStatus): boolean {
  return status === 'Active';
}

/**
 * Check if a listing is leased
 */
export function isListingLeased(status: ListingStatus): boolean {
  return status === 'Leased';
}

// ============================================================================
// Purchase Order Status Helpers
// ============================================================================

/**
 * Check if a purchase order is draft
 */
export function isPurchaseOrderDraft(status: PurchaseOrderStatus): boolean {
  return status === 'draft';
}

/**
 * Check if a purchase order is received
 */
export function isPurchaseOrderReceived(status: PurchaseOrderStatus): boolean {
  return status === 'received';
}

// ============================================================================
// Payment Plan Status Helpers
// ============================================================================

/**
 * Check if a payment plan is active
 */
export function isPaymentPlanActive(status: PaymentPlanStatus): boolean {
  return status === 'active';
}

/**
 * Check if a payment plan is completed
 */
export function isPaymentPlanCompleted(status: PaymentPlanStatus): boolean {
  return status === 'completed';
}

/**
 * Check if a payment plan is defaulted
 */
export function isPaymentPlanDefaulted(status: PaymentPlanStatus): boolean {
  return status === 'defaulted';
}

// ============================================================================
// Installment Status Helpers
// ============================================================================

/**
 * Check if an installment is paid
 */
export function isInstallmentPaid(status: InstallmentStatus): boolean {
  return status === 'paid';
}

/**
 * Check if an installment is overdue
 */
export function isInstallmentOverdue(status: InstallmentStatus): boolean {
  return status === 'overdue';
}

/**
 * Check if an installment is pending
 */
export function isInstallmentPending(status: InstallmentStatus): boolean {
  return status === 'pending';
}

// ============================================================================
// Vendor Status Helpers
// ============================================================================

/**
 * Check if a vendor is active
 */
export function isVendorActive(status?: VendorStatus): boolean {
  return status === 'Active';
}

/**
 * Check if a vendor is suspended
 */
export function isVendorSuspended(status?: VendorStatus): boolean {
  return status === 'Suspended';
}

// ============================================================================
// Owner Status Helpers
// ============================================================================

/**
 * Check if an owner is active
 */
export function isOwnerActive(status: OwnerStatus): boolean {
  return status === 'Active';
}

// ============================================================================
// Unit Status Helpers
// ============================================================================

/**
 * Check if a unit is vacant
 */
export function isUnitVacant(status: UnitStatus): boolean {
  return status === 'Vacant';
}

/**
 * Check if a unit is occupied
 */
export function isUnitOccupied(status: UnitStatus): boolean {
  return status === 'Occupied';
}

// ============================================================================
// Status Badge Color Mapping
// ============================================================================

/**
 * StatusBadge variant type (matches StatusBadge component)
 */
export type StatusVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'blue';

/**
 * Get StatusBadge variant for any status string
 * Centralized mapping for consistent status badge colors across the platform
 */
export function getStatusVariant(status: string): StatusVariant {
  const s = status.toLowerCase().trim();

  // Success states (green)
  if (['completed', 'paid', 'active', 'approved', 'stable', 'current', 'cleared', 'verified', 'leased', 'success', 'passed', 'compliant', 'matched'].includes(s)) {
    return 'success';
  }

  // Warning states (amber/yellow)
  if (['warning', 'late', 'overdue', 'at risk', 'critical', 'notice', 'pending approval', 'vendor assigned', 'open'].includes(s)) {
    return 'warning';
  }

  // Error states (red)
  if (['failed', 'denied', 'rejected', 'cancelled', 'expired', 'terminated', 'past', 'lost', 'defaulted', 'overdue', 'at risk', 'non-compliant', 'non_compliant', 'discrepancy'].includes(s)) {
    return 'error';
  }

  // Info/Blue states (blue)
  if (['pending', 'in progress', 'in_progress', 'review', 'lease-up', 'under review', 'screening', 'pending docs', 'processing'].includes(s)) {
    return 'blue';
  }

  // Neutral states (gray)
  if (['draft', 'inactive', 'closed', 'vacant', 'not started', 'planning', 'scheduled'].includes(s)) {
    return 'neutral';
  }

  // Default to neutral for unknown statuses
  return 'neutral';
}

/**
 * Get StatusBadge variant for specific status types (type-safe versions)
 */
export function getLeaseStatusVariant(status: LeaseStatus | string): StatusVariant {
  const canonical = normalizeLeaseStatus(String(status));
  switch (canonical) {
    case 'Active':
    case 'Renewed':
      return 'success';
    case 'Draft':
      return 'neutral';
    case 'Out_For_Signature':
      return 'blue';
    case 'Executed':
      return 'info';
    case 'Expired':
    case 'Terminated':
      return 'error';
  }
}

export function getPropertyStatusVariant(status: PropertyStatus): StatusVariant {
  return getStatusVariant(status);
}

export function getTenantStatusVariant(status: TenantStatus): StatusVariant {
  return getStatusVariant(status);
}

export function getWorkOrderStatusVariant(status: WorkOrderStatus): StatusVariant {
  return getStatusVariant(status);
}

export function getBillStatusVariant(status: BillStatus): StatusVariant {
  return getStatusVariant(status);
}

export function getReceivableStatusVariant(status: ReceivableStatus): StatusVariant {
  return getStatusVariant(status);
}

export function getPaymentStatusVariant(status: PaymentStatus): StatusVariant {
  return getStatusVariant(status);
}

export function getApplicationStatusVariant(status: ApplicationStatus): StatusVariant {
  return getStatusVariant(status);
}

// ============================================================================
// Maintenance (Work Order) traffic-light — red only for action required
// ============================================================================

/**
 * Maintenance status badge variant. Red is reserved for action-required only; not used for decoration.
 * Green = healthy/completed; Amber = attention (pending approval, pending vendor); Red = never for status alone (use priority + context for red).
 */
export function getMaintenanceStatusVariant(status: string): StatusVariant {
  const s = status.toLowerCase().trim();
  if (['completed', 'verified', 'approved', 'closed'].includes(s)) return 'success';
  if (['pending approval', 'open', 'vendor assigned'].includes(s)) return 'warning';
  if (['in progress', 'in_progress', 'pending'].includes(s)) return 'blue';
  return 'neutral';
}

/**
 * Maintenance priority badge variant. Red only for action-required: Critical, Urgent.
 * Amber for High (attention); neutral for Medium/Low.
 */
export function getMaintenancePriorityVariant(priority: string): StatusVariant {
  const p = priority.toLowerCase().trim();
  if (p === 'critical' || p === 'urgent') return 'error';
  if (p === 'high') return 'warning';
  return 'neutral';
}
